/**
 * "Fehler merken" in the referee PWA (user decision 2026-11-01, option C).
 *
 * One tap during the training, no questions: a `bug` marker goes into the
 * flight recorder (with a full DOM snapshot now and ~4 s later — the
 * seconds before are in the running recording) and a bug report is stored
 * on the server. The referee describes it after the training
 * (BugReportsScreen, or "Meine Trainings" on the web).
 *
 * The server call goes through a small localStorage queue so a press while
 * offline is not lost (the id is generated here, a retried create is a
 * no-op on the server).
 */
import { getTelemetryClient, markBug } from '@flm9/telemetry-client';
import { api, ApiError, isOffline, UnauthenticatedError } from './api';

const QUEUE_KEY = 'flm9.bugReports.queue';
const USER_KEY = 'flm9.bugReports.user';
const DEVICE_KEY = 'flm9.telemetry.device';

type Pending = Parameters<typeof api.createBugReport>[0];

function readQueue(): Pending[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? (parsed as Pending[]) : [];
  } catch {
    return [];
  }
}

function writeQueue(items: Pending[]): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(items.slice(-50)));
  } catch {
    // storage full / blocked: the marker in the recording still exists
  }
}

function deviceId(): string {
  const fromClient = getTelemetryClient()?.deviceId;
  if (fromClient) return fromClient;
  try {
    return localStorage.getItem(DEVICE_KEY) ?? 'unknown';
  } catch {
    return 'unknown';
  }
}

/** Last known signed-in user (owner of new presses); refreshed before each drain. */
function cachedUserId(): string | null {
  try {
    return localStorage.getItem(USER_KEY);
  } catch {
    return null;
  }
}

async function refreshUserId(): Promise<string | null> {
  try {
    const id = await api.myUserId();
    try {
      if (id) localStorage.setItem(USER_KEY, id);
      else localStorage.removeItem(USER_KEY);
    } catch {
      // storage blocked: fine, the server still binds the report
    }
    return id;
  } catch {
    return cachedUserId(); // offline: last known
  }
}

/** Number of presses not yet on the server. */
export function pendingBugReports(): number {
  return readQueue().length;
}

/** The button: marker + snapshots now, server report via the queue. */
export function recordBug(opts: {
  sessionId: string;
  screen: string;
  context?: Record<string, unknown>;
}): void {
  const id = crypto.randomUUID();
  const context = opts.context ?? {};
  const { traceId } = markBug(id, { screen: opts.screen, ...context });
  writeQueue([
    ...readQueue(),
    {
      id,
      sessionId: opts.sessionId,
      surface: 'referee',
      deviceId: deviceId(),
      reportedAt: new Date().toISOString(),
      screen: opts.screen,
      context,
      traceId,
      reporterUserId: cachedUserId(),
    },
  ]);
  void drainBugReports();
}

/**
 * keep-stop: transient (offline, signed out, server trouble) — keep and stop
 * for now; keep-skip: another account's report (409), kept for its owner;
 * drop: definitively refused (bad input, training gone, not allowed).
 */
function outcomeOf(err: unknown): 'keep-stop' | 'keep-skip' | 'drop' {
  if (err instanceof UnauthenticatedError || isOffline(err)) return 'keep-stop';
  if (err instanceof ApiError) {
    if (err.status === 409) return 'keep-skip';
    if ([400, 403, 404, 422].includes(err.status)) return 'drop';
  }
  return 'keep-stop'; // 5xx, timeouts, anything unknown
}

const uploadListeners = new Set<() => void>();
/** Called after a queued press reached the server (e.g. to reload a list). */
export function onBugReportUploaded(listener: () => void): () => void {
  uploadListeners.add(listener);
  return () => {
    uploadListeners.delete(listener);
  };
}

let draining = false;

/** Send queued presses; keeps the ones that failed for lack of network. */
export async function drainBugReports(): Promise<void> {
  if (draining) return;
  draining = true;
  try {
    // Always refresh who is signed in (also with an empty queue): the next
    // press must carry the CURRENT account, not the previous one.
    const me = await refreshUserId();
    if (readQueue().length === 0) return;
    for (const item of readQueue()) {
      // Another account's press waits for its owner to sign in again.
      if (item.reporterUserId && me && item.reporterUserId !== me) continue;
      try {
        await api.createBugReport(item);
        writeQueue(readQueue().filter((q) => q.id !== item.id));
        for (const l of [...uploadListeners]) l();
      } catch (err) {
        const outcome = outcomeOf(err);
        if (outcome === 'keep-stop') return;
        if (outcome === 'drop') writeQueue(readQueue().filter((q) => q.id !== item.id));
      }
    }
  } finally {
    draining = false;
  }
}

/** Retry on reconnect and every 30 s (App mounts this once). */
export function startBugReportSync(): () => void {
  void refreshUserId();
  void drainBugReports();
  const onOnline = () => void drainBugReports();
  window.addEventListener('online', onOnline);
  const timer = setInterval(() => void drainBugReports(), 30_000);
  return () => {
    window.removeEventListener('online', onOnline);
    clearInterval(timer);
  };
}
